import type { FilterState, ResourceType, Severity, Environment } from './types';

const VALID_PROVIDERS = ['gcp', 'aws'] as const;
const VALID_SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
const VALID_ENVIRONMENTS = ['dev', 'qa', 'prd', 'other'] as const;

/**
 * Serializes a FilterState into URLSearchParams.
 * Omits empty arrays and empty strings from the output.
 * Array fields use comma-separated values.
 */
export function serialize(filterState: FilterState): URLSearchParams {
  const params = new URLSearchParams();

  if (filterState.providers.length > 0) {
    params.set('provider', filterState.providers.join(','));
  }

  if (filterState.resourceTypes.length > 0) {
    params.set('type', filterState.resourceTypes.join(','));
  }

  if (filterState.regions.length > 0) {
    params.set('region', filterState.regions.join(','));
  }

  if (filterState.projectsOrAccounts.length > 0) {
    params.set('project', filterState.projectsOrAccounts.join(','));
  }

  if (filterState.environments.length > 0) {
    params.set('environment', filterState.environments.join(','));
  }

  if (filterState.severities.length > 0) {
    params.set('severity', filterState.severities.join(','));
  }

  if (filterState.search.length > 0) {
    params.set('search', filterState.search);
  }

  return params;
}

/**
 * Deserializes URLSearchParams into a FilterState.
 * Parses comma-separated values back into arrays.
 * Returns default empty arrays/strings for missing params.
 * Validates provider values are 'gcp' or 'aws' only.
 */
export function deserialize(params: URLSearchParams): FilterState {
  const providerParam = params.get('provider');
  const providers = providerParam
    ? providerParam
        .split(',')
        .filter((v): v is 'gcp' | 'aws' =>
          VALID_PROVIDERS.includes(v as 'gcp' | 'aws')
        )
    : [];

  const typeParam = params.get('type');
  const resourceTypes = typeParam
    ? (typeParam.split(',').filter((v) => v.length > 0) as ResourceType[])
    : [];

  const regionParam = params.get('region');
  const regions = regionParam
    ? regionParam.split(',').filter((v) => v.length > 0)
    : [];

  const projectParam = params.get('project');
  const projectsOrAccounts = projectParam
    ? projectParam.split(',').filter((v) => v.length > 0)
    : [];

  const environmentParam = params.get('environment');
  const environments = environmentParam
    ? environmentParam
        .split(',')
        .filter((v): v is Environment =>
          VALID_ENVIRONMENTS.includes(v as Environment)
        )
    : [];

  const severityParam = params.get('severity');
  const severities = severityParam
    ? severityParam
        .split(',')
        .filter((v): v is Severity =>
          VALID_SEVERITIES.includes(v as Severity)
        )
    : [];

  const search = params.get('search') ?? '';

  return {
    providers,
    resourceTypes,
    regions,
    projectsOrAccounts,
    environments,
    severities,
    search,
  };
}
